import { Navigate, useLocation, type RouteObject } from 'react-router';
import { EmptyState, LoadingState } from './components/states';
import { PageFrame } from './components/page-frame';
import { LoginPage } from './pages/login';
import { SetupPage } from './pages/setup';
import { SetPasswordPage } from './pages/set-password';
import { ForgotPasswordPage } from './pages/forgot-password';
import { ChangePasswordPage } from './features/account/change-password';
import { useSetupStatus } from './features/setup/api';
import { SignedInHome } from './pages/signed-in-home';
import type { LoginLocationState } from './session/auth-actions';
import {
  AuthenticatedLayout,
  ByRole,
  RequirePermission,
  SessionCheckError,
} from './session/guards';
import { PortalShell, ShellNotFound } from './shell/admin-shell';
import { ServiceCreatePage } from './features/services/service-create';
import { ServiceDetail } from './features/services/service-detail';
import { ServicesOverview } from './features/services/services-overview';
import { CheckCreatePage } from './features/monitoring/check-form';
import { AlertDetail } from './features/monitoring/alert-detail';
import { AlertsOverview } from './features/monitoring/alerts-overview';
import { CheckDetail } from './features/monitoring/check-detail';
import { MonitoringOverview } from './features/monitoring/monitoring-overview';
import { RenewalDetail } from './features/renewals/renewal-detail';
import { OpenRenewalPage } from './features/renewals/renewal-forms';
import { RenewalsOverview } from './features/renewals/renewals-overview';
import { InfrastructureOverview } from './features/infrastructure/infrastructure-overview';
import { ResourceDetailPage } from './features/infrastructure/resource-detail';
import { ResourceCreatePage } from './features/infrastructure/resource-forms';
import { PaymentAccountPage } from './features/payment-account/payment-account-page';
import { UserCreatePage } from './features/users/user-create';
import { UsersOverview } from './features/users/users-overview';
import { ActivityOverview } from './features/activity/activity-overview';
import { MyActivityPage } from './features/activity/my-activity';
import { Dashboard } from './features/dashboard/dashboard';
import { IctDashboard } from './features/dashboard/ict-dashboard';
import { MailboxesPage } from './features/mailboxes/mailboxes-page';
import { MailboxCreatePage } from './features/mailboxes/mailbox-create';
import { MailboxSizePage } from './features/mailboxes/mailbox-size';
import { useSession } from './session/session';

/**
 * Routes.
 *
 *   /        → /app when signed in, otherwise /login
 *   /login   → sign-in; a signed-in visitor is sent on to /app (or where they were going). While
 *              the portal has no Admin yet, a signed-out visitor is sent to /setup instead.
 *   /setup   → first-run setup: create the first Admin with the operator's setup token. Closed
 *              (sent on to /login) as soon as any Admin exists.
 *   /account/set-password#<token> → where invitation and password reset links open (public).
 *   /forgot-password → ask for a password reset link (public; same answer for every address).
 *   /app     → the portal shell for both views, starting at /app/dashboard.
 *   /app/<section> → one route tree for Admin and ICT (internal role `operations`). Each section is
 *              guarded by the same permission its navigation entry uses in shell/navigation.ts, so
 *              a section opens only for a session holding that permission (a shell test checks
 *              every entry). ICT holds no permission for Infrastructure, Monitoring, Users or the
 *              full Activity feed, so those refuse it before any request is made. Shared pages hide
 *              management actions by permission; /app/dashboard differs by view (ByRole).
 *
 * Guards are for navigation only. The API checks every request's session and permission itself.
 */

export function RootRedirect() {
  const session = useSession();
  if (session.status === 'loading') return <LoadingState label="Checking your session…" />;
  if (session.status === 'error') {
    return <SessionCheckError message={session.error.message} onRetry={session.retry} />;
  }
  return <Navigate to={session.status === 'authenticated' ? '/app' : '/login'} replace />;
}

/** Only paths inside the signed-in area are valid return targets (no open redirects). */
function safeReturnPath(from: string | undefined): string {
  return from && (from === '/app' || from.startsWith('/app/')) ? from : '/app';
}

export function LoginRoute() {
  const session = useSession();
  const location = useLocation();
  const state = (location.state ?? {}) as LoginLocationState;

  const signedIn = session.status === 'authenticated';
  // First-run setup: while no Admin exists, sign-in cannot work, so send the visitor to /setup.
  // If the check fails, sign-in is still shown (the server stays the authority either way).
  const setup = useSetupStatus({ enabled: session.status !== 'loading' && !signedIn });

  if (session.status === 'loading') return <LoadingState label="Checking your session…" />;
  if (signedIn) return <Navigate to={safeReturnPath(state.from)} replace />;
  // Sign-in shows at once; only a confirmed "no Admin yet" moves the visitor on to /setup.
  if (setup.data?.setupRequired) return <Navigate to="/setup" replace />;
  // Unauthenticated, or the session could not be checked: signing in is still possible.
  return <LoginPage {...(state.reason ? { reason: state.reason } : {})} />;
}

export function SetupRoute() {
  const session = useSession();
  if (session.status === 'loading') return <LoadingState label="Checking your session…" />;
  if (session.status === 'authenticated') return <Navigate to="/app" replace />;
  return <SetupPage />;
}

export function NotFound() {
  return (
    <PageFrame>
      <EmptyState title="Page not found" description="There is nothing at this address." />
    </PageFrame>
  );
}

export const routes: RouteObject[] = [
  { path: '/', element: <RootRedirect /> },
  { path: '/login', element: <LoginRoute /> },
  { path: '/setup', element: <SetupRoute /> },
  { path: '/account/set-password', element: <SetPasswordPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  {
    path: '/app',
    element: <AuthenticatedLayout />,
    children: [
      { index: true, element: <SignedInHome /> },
      {
        element: <PortalShell />,
        children: [
          {
            path: 'services',
            element: <RequirePermission permission="services.view" />,
            children: [
              { index: true, element: <ServicesOverview /> },
              {
                path: 'new',
                element: (
                  <RequirePermission permission="services.manage">
                    <ServiceCreatePage />
                  </RequirePermission>
                ),
              },
              { path: ':serviceId', element: <ServiceDetail /> },
            ],
          },
          {
            path: 'monitoring',
            element: <RequirePermission permission="monitoring.view" />,
            children: [
              { index: true, element: <MonitoringOverview /> },
              {
                path: 'new',
                element: (
                  <RequirePermission permission="monitoring.manage">
                    <CheckCreatePage />
                  </RequirePermission>
                ),
              },
              { path: 'alerts', element: <AlertsOverview /> },
              { path: 'alerts/:alertId', element: <AlertDetail /> },
              { path: ':checkId', element: <CheckDetail /> },
            ],
          },
          {
            path: 'renewals',
            element: <RequirePermission permission="renewals.view" />,
            children: [
              { index: true, element: <RenewalsOverview /> },
              {
                path: 'new',
                element: (
                  <RequirePermission permission="renewals.manage">
                    <OpenRenewalPage />
                  </RequirePermission>
                ),
              },
              { path: ':renewalId', element: <RenewalDetail /> },
            ],
          },
          {
            path: 'infrastructure',
            element: <RequirePermission permission="infrastructure.view" />,
            children: [
              { index: true, element: <InfrastructureOverview /> },
              {
                path: 'new',
                element: (
                  <RequirePermission permission="infrastructure.manage">
                    <ResourceCreatePage />
                  </RequirePermission>
                ),
              },
              { path: ':resourceId', element: <ResourceDetailPage /> },
            ],
          },
          {
            path: 'payment-account',
            element: (
              <RequirePermission permission="payment_account.view">
                <PaymentAccountPage />
              </RequirePermission>
            ),
          },
          {
            path: 'users',
            element: <RequirePermission permission="users.manage" />,
            children: [
              { index: true, element: <UsersOverview /> },
              { path: 'new', element: <UserCreatePage /> },
            ],
          },
          {
            path: 'activity',
            element: (
              <RequirePermission permission="audit.view">
                <ActivityOverview />
              </RequirePermission>
            ),
          },
          {
            path: 'dashboard',
            element: (
              <RequirePermission permission="services.view">
                <ByRole admin={<Dashboard />} ict={<IctDashboard />} />
              </RequirePermission>
            ),
          },
          {
            path: 'mailboxes',
            element: <RequirePermission permission="mail.mailboxes.view" />,
            children: [
              { index: true, element: <MailboxesPage /> },
              {
                path: 'new',
                element: (
                  <RequirePermission permission="mail.mailboxes.manage">
                    <MailboxCreatePage />
                  </RequirePermission>
                ),
              },
              {
                path: ':address/size',
                element: (
                  <RequirePermission permission="mail.mailboxes.manage">
                    <MailboxSizePage />
                  </RequirePermission>
                ),
              },
            ],
          },
          {
            path: 'my-activity',
            element: (
              <RequirePermission permission="audit.view_own">
                <MyActivityPage />
              </RequirePermission>
            ),
          },
          // Every signed-in user, Admin or ICT, may change their own password.
          { path: 'account/password', element: <ChangePasswordPage /> },
          { path: '*', element: <ShellNotFound /> },
        ],
      },
    ],
  },
  { path: '*', element: <NotFound /> },
];
